import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { getSession, subscribe } from '@/auth/session';
import { AlertsStrip } from '@/components/AlertsStrip';
import { Select } from '@/components/Select';
import { TabNav } from '@/components/TabNav';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { AdminUsersPage } from '@/pages/AdminUsersPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { GlossaryPage } from '@/pages/GlossaryPage';
import { ExternalAccountsPage } from '@/pages/ops/ExternalAccountsPage';
import { HomePage } from '@/pages/HomePage';
import { AccountDetailPage } from '@/pages/AccountDetailPage';
import { UserDetailPage } from '@/pages/UserDetailPage';
import { AccountsPage } from '@/pages/AccountsPage';
import { AtRiskPage } from '@/pages/AtRiskPage';
import { AcquisitionPage } from '@/pages/AcquisitionPage';
import { FrustrationPage } from '@/pages/FrustrationPage';
import { GeographyPage } from '@/pages/GeographyPage';
import { InsightsPage } from '@/pages/InsightsPage';
import { LivePage } from '@/pages/LivePage';
import { DomainsPage } from '@/pages/DomainsPage';
import { BehaviourPage } from '@/pages/BehaviourPage';
import { JourneysPage } from '@/pages/JourneysPage';
import { LeadDetailPage } from '@/pages/LeadDetailPage';
import { LeadsPage } from '@/pages/LeadsPage';
import { LoginPage } from '@/pages/LoginPage';
import { OverviewPage } from '@/pages/OverviewPage';
import { PagesPage } from '@/pages/PagesPage';
import { ExplorePage } from '@/pages/ExplorePage';
import { FeaturesPage } from '@/pages/FeaturesPage';
import { ServicesPage } from '@/pages/ServicesPage';
import { SourcesPage } from '@/pages/SourcesPage';
import { AuditPage } from '@/pages/ops/AuditPage';
import { CostsPage } from '@/pages/ops/CostsPage';
import { EnvironmentHealthPage } from '@/pages/ops/EnvironmentHealthPage';
import { EnvironmentsPage } from '@/pages/ops/EnvironmentsPage';
import { GatewaysPage } from '@/pages/ops/GatewaysPage';
import { ImportKeysPage } from '@/pages/ops/ImportKeysPage';
import { IndustryPackDetailPage, IndustryPacksPage } from '@/pages/ops/IndustryPacksPage';
import { MessagingAdminPage } from '@/pages/ops/MessagingAdminPage';
import { MoneyOperationsPage } from '@/pages/ops/MoneyOperationsPage';
import { MoneyPage } from '@/pages/ops/MoneyPage';
import { OffersPage } from '@/pages/ops/OffersPage';
import { PricingPage } from '@/pages/ops/PricingPage';
import { ProvisioningJobPage } from '@/pages/ops/ProvisioningJobPage';
import { ProvisioningJobsPage } from '@/pages/ops/ProvisioningJobsPage';
import { ProvisioningWizardPage } from '@/pages/ops/ProvisioningWizardPage';
import { RetentionPage } from '@/pages/ops/RetentionPage';
import { StepCatalogPage } from '@/pages/ops/StepCatalogPage';
import { TenantDetailPage } from '@/pages/ops/TenantDetailPage';
import { TenantsPage } from '@/pages/ops/TenantsPage';
import { Shell } from './Shell';

export function App() {
  // Subscribing to the session store means login/logout/expiry all swap the tree
  // from one place — no route guard needs to duplicate the check.
  const session = useSyncExternalStore(subscribe, getSession, getSession);

  if (!session) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  // A seeded or reset account changes its password before anything else — the server refuses every other call until
  // it does, so the console is not even drawn.
  if (session.user.must_change_password) {
    return <ChangePasswordPage forced />;
  }

  return <AuthedShell />;
}

/** The website site picker — shown on the website pages only (it means nothing anywhere else). */
function SitePicker({ siteKeys, value, onChange }: { siteKeys: string[]; value: string | null; onChange: (key: string) => void }) {
  if (siteKeys.length <= 1) return null;
  return (
    <div className="row">
      <Select label="Site" value={value ?? ''} onChange={onChange} options={siteKeys.map((key) => ({ value: key, label: key }))} inline />
    </div>
  );
}

/**
 * Wraps the four analytics tabs: renders the tab strip, and short-circuits to an empty state when
 * no source is connected — so each page can assume a non-null siteKey.
 */
function AnalyticsTab({ siteKey, picker, loading, children }: { siteKey: string | null; picker?: ReactNode; loading?: boolean; children: ReactNode }) {
  if (!siteKey) {
    // Loading or failed is not "none connected" — that sentence would send someone to add a source that exists.
    return (
      <div className="empty-state t-body">
        {loading ? <span className="spinner" aria-label="Loading" /> : 'No sources connected yet. Add one on the Sources page to see analytics.'}
      </div>
    );
  }

  return (
    <>
      {picker}
      <TabNav />
      {/* Above the content on EVERY tab: an ingestion failure invalidates whatever is drawn
          below it, so it has to be seen before the charts are interpreted. */}
      <AlertsStrip siteKey={siteKey} />
      {children}
    </>
  );
}

/**
 * The Leads section shell.
 *
 * Same site guard as the analytics tabs and nothing else: no TabNav, because Leads is not one of
 * the traffic reports, and no AlertsStrip, because an ingestion failure has no bearing on rows
 * that arrive through the lead sync rather than through a provider.
 */
function LeadsSection({ siteKey, picker, loading, children }: { siteKey: string | null; picker?: ReactNode; loading?: boolean; children: ReactNode }) {
  if (!siteKey) {
    return (
      <div className="empty-state t-body">
        {loading ? <span className="spinner" aria-label="Loading" /> : 'No sources connected yet. Add one on the Sources page — leads are scoped to a site.'}
      </div>
    );
  }

  return <>{picker}{children}</>;
}

function AuthedShell() {
  // The site selector is driven by whatever sources are actually connected — never a
  // hardcoded "tavi-landing".
  const { data: sources, isPending: sourcesPending, isError: sourcesFailed } = useQuery({ queryKey: ['sources'], queryFn: api.listSources });
  // Still loading (or failed): the website pages say so instead of "no sources connected".
  const sourcesUnknown = sourcesPending || sourcesFailed;

  const siteKeys = [...new Set((sources ?? []).map((s) => s.site_key))];
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const activeSite = siteKey ?? siteKeys[0] ?? null;

  useEffect(() => {
    if (siteKey === null && siteKeys.length > 0) setSiteKey(siteKeys[0]!);
  }, [siteKey, siteKeys]);

  const picker = <SitePicker siteKeys={siteKeys} value={activeSite} onChange={setSiteKey} />;
  const tab = (page: ReactNode) => <AnalyticsTab siteKey={activeSite} picker={picker} loading={sourcesUnknown}>{page}</AnalyticsTab>;

  return (
    <Shell>
      <Routes>
        {/* Home: what needs attention across tenants, money and servers (2026-09-30). The website traffic overview
            that used to live here is /website — the old /live, /domains … tab links still open as before. */}
        <Route path="/" element={<HomePage />} />
        <Route path="/account/password" element={<ChangePasswordPage />} />
        <Route path="/settings/admins" element={<AdminUsersPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/help/glossary" element={<GlossaryPage />} />

        {/* The four analytics tabs share one data query (useOverview), so switching
            between them costs no network request. */}
        <Route path="/website" element={tab(<OverviewPage siteKey={activeSite!} />)} />
        <Route path="/live" element={tab(<LivePage siteKey={activeSite!} />)} />
        {/* Sidebar section, so no TabNav — but still site-scoped, and still gated on a source
            being connected, because a lead belongs to a site the same way a session does. */}
        <Route path="/leads" element={<LeadsSection siteKey={activeSite} picker={picker} loading={sourcesUnknown}><LeadsPage siteKey={activeSite!} /></LeadsSection>} />
        <Route path="/leads/:leadId" element={<LeadsSection siteKey={activeSite} loading={sourcesUnknown}><LeadDetailPage /></LeadsSection>} />
        <Route path="/domains" element={tab(<DomainsPage siteKey={activeSite!} />)} />
        <Route path="/journeys" element={tab(<JourneysPage siteKey={activeSite!} />)} />
        <Route path="/behaviour" element={tab(<BehaviourPage siteKey={activeSite!} />)} />
        <Route path="/acquisition" element={tab(<AcquisitionPage siteKey={activeSite!} />)} />
        <Route path="/geography" element={tab(<GeographyPage siteKey={activeSite!} />)} />
        <Route path="/insights" element={tab(<InsightsPage siteKey={activeSite!} />)} />
        <Route path="/pages" element={tab(<PagesPage siteKey={activeSite!} />)} />
        <Route path="/frustration" element={tab(<FrustrationPage siteKey={activeSite!} />)} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/at-risk" element={<AtRiskPage />} />
        <Route path="/accounts/:tenantId" element={<AccountDetailPage />} />
        {/* One person's session history. The API behind it needs telemetry:agent_detail, and
            the page states that rather than rendering blank when a viewer arrives. */}
        <Route path="/accounts/:tenantId/users/:userId" element={<UserDetailPage />} />
        {/* Platform-level, not site-scoped: feature adoption spans every tenant, so no
            AnalyticsTab wrapper and no source gate. Same posture as Accounts and Services. */}
        <Route path="/features" element={<FeaturesPage />} />
        {/* Platform-level like Features: cross-tenant, so no AnalyticsTab and no site gate.
            The page itself states the explore:read requirement rather than rendering blank. */}
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/sources" element={<SourcesPage />} />

        {/* Tenant ops. Each page gates itself with RequirePermission and STATES what it needs when
            a role lacks it — a direct link never renders blank or a wall of 403s. The pathless layout
            route only adds the .ops-page scope (display: contents — no box of its own). */}
        <Route element={<div className="ops-page"><Outlet /></div>}>
          <Route path="/ops/tenants" element={<TenantsPage />} />
          <Route path="/ops/tenants/:env/:tenantId" element={<TenantDetailPage />} />
          <Route path="/ops/provisioning" element={<ProvisioningJobsPage />} />
          <Route path="/ops/provisioning/new" element={<ProvisioningWizardPage />} />
          <Route path="/ops/provisioning/jobs/:id" element={<ProvisioningJobPage />} />
          <Route path="/ops/provisioning/catalog" element={<StepCatalogPage />} />
          <Route path="/ops/environments" element={<EnvironmentsPage />} />
          <Route path="/ops/environments/:env" element={<EnvironmentHealthPage />} />
          <Route path="/ops/import-keys" element={<ImportKeysPage />} />
          <Route path="/ops/industry-packs" element={<IndustryPacksPage />} />
          <Route path="/ops/industry-packs/:key" element={<IndustryPackDetailPage />} />
          <Route path="/ops/money" element={<MoneyPage />} />
          <Route path="/ops/costs" element={<CostsPage />} />
          <Route path="/ops/pricing" element={<PricingPage />} />
          <Route path="/ops/offers" element={<OffersPage />} />
          <Route path="/ops/gateways" element={<GatewaysPage />} />
          <Route path="/ops/retention" element={<RetentionPage />} />
          <Route path="/ops/money-operations" element={<MoneyOperationsPage />} />
          <Route path="/ops/messaging" element={<MessagingAdminPage />} />
          <Route path="/ops/external-accounts" element={<ExternalAccountsPage />} />
          <Route path="/ops/audit" element={<AuditPage />} />
        </Route>

        {/* Short names for the pages people type or share. */}
        <Route path="/tenants" element={<Navigate to="/ops/tenants" replace />} />
        <Route path="/tenants/new" element={<Navigate to="/ops/provisioning/new" replace />} />
        <Route path="/overview" element={<Navigate to="/website" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
